import React, { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Sidebar from '@/components/layout/Sidebar';
import TeacherDashboard from '@/components/dashboard/TeacherDashboard';
import AdminDashboard from '@/components/dashboard/AdminDashboard';
import LessonPlanGenerator from '@/components/ai/LessonPlanGenerator';
import QuizGenerator from '@/components/ai/QuizGenerator';
import PlagiarismChecker from '@/components/ai/PlagiarismChecker';
import FloatingChatbot, { FloatingChatbotRef } from '@/components/ai/FloatingChatbot';
import EnhancedAIAssistant from '@/components/ai/EnhancedAIAssistant';
import ClassesManager from '@/components/classes/ClassesManager';
import TeacherManagement from '@/components/admin/TeacherManagement';
import QuestionsPoolManager from '@/components/questions/QuestionsPoolManager';
import PresentationGenerator from '@/components/ai/PresentationGenerator';
import GameifyManager from '@/components/gameify/GameifyManager';
import ErrorBoundary from '@/components/ErrorBoundary';
import { motion } from 'motion/react';
import { useIsMobile } from '@/hooks/use-mobile';

const TeacherApp: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFloatingChatOpen, setIsFloatingChatOpen] = useState(false);
  const { isSuperAdmin, isAdmin, adminStatus, hasAIAccess } = useAuth();
  const isMobile = useIsMobile();
  const floatingChatbotRef = useRef<FloatingChatbotRef>(null);

  // Enhanced debug logging
  console.log('TeacherApp - Current activeTab:', activeTab);
  console.log('TeacherApp - isSuperAdmin:', isSuperAdmin);
  console.log('TeacherApp - isAdmin:', isAdmin);
  console.log('TeacherApp - adminStatus:', adminStatus);
  console.log('TeacherApp - hasAIAccess basic:', hasAIAccess('basic'));
  console.log('TeacherApp - hasAIAccess advanced:', hasAIAccess('advanced'));

  const handleOpenFloatingChat = () => {
    console.log('🚀 Opening floating chat...');
    floatingChatbotRef.current?.openChat();
  };

  const handleContinueConversation = (conversationId: string) => {
    console.log('🔄 Continuing conversation:', conversationId);
    floatingChatbotRef.current?.openChatWithConversation(conversationId);
  };

  const renderContent = () => {
    console.log('TeacherApp - renderContent called with activeTab:', activeTab);

    switch (activeTab) {
      case 'dashboard':
        return isAdmin ?
        <AdminDashboard setActiveTab={setActiveTab} data-id="t29w1jfag" data-path="src/pages/TeacherApp.tsx" /> :
        <TeacherDashboard setActiveTab={setActiveTab} data-id="4azzo036n" data-path="src/pages/TeacherApp.tsx" />;

      case 'classes':
        return <ClassesManager setActiveTab={setActiveTab} data-id="eltbxwwpy" data-path="src/pages/TeacherApp.tsx" />;

      case 'questions-pool':
        console.log('TeacherApp - Rendering QuestionsPoolManager');
        console.log('TeacherApp - Admin status check - isSuperAdmin:', isSuperAdmin, 'isAdmin:', isAdmin);
        return <QuestionsPoolManager data-id="ul033aqnx" data-path="src/pages/TeacherApp.tsx" />;

      case 'gameify':
        return (
          <ErrorBoundary data-id="z1bodav2s" data-path="src/pages/TeacherApp.tsx">
            <GameifyManager data-id="j8pm35om0" data-path="src/pages/TeacherApp.tsx" />
          </ErrorBoundary>);


      case 'lesson-plans':
        // Admin users (Level 2) have access to all AI features
        return hasAIAccess('advanced') || isAdmin ?
        <ErrorBoundary data-id="iifax6nax" data-path="src/pages/TeacherApp.tsx">
            <LessonPlanGenerator data-id="1hbijgrm2" data-path="src/pages/TeacherApp.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="pb80elxsz" data-path="src/pages/TeacherApp.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="5k8bt37id" data-path="src/pages/TeacherApp.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="83koje0b3" data-path="src/pages/TeacherApp.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="zo3ms8x25" data-path="src/pages/TeacherApp.tsx">
                Lesson Plan Generator requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'quizzes':
        return hasAIAccess('advanced') || isAdmin ?
        <ErrorBoundary data-id="uffgn4sfl" data-path="src/pages/TeacherApp.tsx">
            <QuizGenerator data-id="rzv9enhth" data-path="src/pages/TeacherApp.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="bp10w01of" data-path="src/pages/TeacherApp.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="lza81rc9j" data-path="src/pages/TeacherApp.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="5xb8zfbbz" data-path="src/pages/TeacherApp.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="v4qcaej7y" data-path="src/pages/TeacherApp.tsx">
                Quiz Generator requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'presentation-generator':
        return hasAIAccess('advanced') || isAdmin ?
        <ErrorBoundary data-id="az0b51gya" data-path="src/pages/TeacherApp.tsx">
            <PresentationGenerator data-id="yy0jk5tr7" data-path="src/pages/TeacherApp.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="bnhl5nemo" data-path="src/pages/TeacherApp.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="dplu9zsvm" data-path="src/pages/TeacherApp.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="rfuorcr6u" data-path="src/pages/TeacherApp.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="xc71tm40d" data-path="src/pages/TeacherApp.tsx">
                Presentation Generator requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'plagiarism':
        return hasAIAccess('advanced') || isAdmin ?
        <ErrorBoundary data-id="ra5x9u5g1" data-path="src/pages/TeacherApp.tsx">
            <PlagiarismChecker data-id="e4dfpzvw8" data-path="src/pages/TeacherApp.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="ouru4srwh" data-path="src/pages/TeacherApp.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="bubk2dvki" data-path="src/pages/TeacherApp.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="abk2928dt" data-path="src/pages/TeacherApp.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="2230i7lg1" data-path="src/pages/TeacherApp.tsx">
                Plagiarism Checker requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'teachers':
        return <TeacherManagement data-id="raek42d7a" data-path="src/pages/TeacherApp.tsx" />;

      case 'superadmin':
        console.log('TeacherApp - Rendering Super Admin page');
        return (
          <div className="text-center py-12" data-id="k6jc93cv4" data-path="src/pages/TeacherApp.tsx">
            <h2 className="text-2xl font-bold mb-4" data-id="okarzdw2v" data-path="src/pages/TeacherApp.tsx">Super Admin Dashboard</h2>
            <p className="text-gray-600" data-id="djynipnjg" data-path="src/pages/TeacherApp.tsx">Super admin interface coming soon...</p>
          </div>);


      case 'schools':
        return (
          <div className="text-center py-12" data-id="o7hh5hel7" data-path="src/pages/TeacherApp.tsx">
            <h2 className="text-2xl font-bold mb-4" data-id="scf8sbae6" data-path="src/pages/TeacherApp.tsx">School Management</h2>
            <p className="text-gray-600" data-id="71jlojfr4" data-path="src/pages/TeacherApp.tsx">School management interface coming soon...</p>
          </div>);


      case 'settings':
        return (
          <div className="text-center py-12" data-id="2igbefwqi" data-path="src/pages/TeacherApp.tsx">
            <h2 className="text-2xl font-bold mb-4" data-id="ah3nwi8lv" data-path="src/pages/TeacherApp.tsx">System Settings</h2>
            <p className="text-gray-600" data-id="7nxbtapfj" data-path="src/pages/TeacherApp.tsx">Settings interface coming soon...</p>
          </div>);


      case 'chat':
        return (
          <ErrorBoundary data-id="wsa9kn4zj" data-path="src/pages/TeacherApp.tsx">
            <EnhancedAIAssistant
              onOpenFloatingChat={handleOpenFloatingChat}
              onContinueConversation={handleContinueConversation} data-id="xsxt9shpb" data-path="src/pages/TeacherApp.tsx" />

          </ErrorBoundary>);


      default:
        console.log('TeacherApp - Default case hit with activeTab:', activeTab);
        console.log('TeacherApp - This should not happen if questions-pool is a valid case');
        return (
          <div className="text-center py-12" data-id="25aqltspi" data-path="src/pages/TeacherApp.tsx">
            <h2 className="text-2xl font-bold mb-4" data-id="o6twdryc3" data-path="src/pages/TeacherApp.tsx">Page Not Found</h2>
            <p className="text-gray-600" data-id="eupsd9mso" data-path="src/pages/TeacherApp.tsx">The requested page could not be found.</p>
            <p className="text-sm text-gray-500 mt-2" data-id="w7zvmiqmp" data-path="src/pages/TeacherApp.tsx">Active tab: {activeTab}</p>
          </div>);

    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-id="4yf5qnfku" data-path="src/pages/TeacherApp.tsx">
      <Navbar data-id="ebdqvjn1r" data-path="src/pages/TeacherApp.tsx" />
      <div className={`flex h-[calc(100vh-4rem)] ${isMobile ? 'flex-col' : ''}`} data-id="c1gfes5hb" data-path="src/pages/TeacherApp.tsx">
        {/* Desktop: Show sidebar normally, Mobile: Sidebar becomes bottom nav */}
        {!isMobile &&
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed} data-id="jwz0rn2mo" data-path="src/pages/TeacherApp.tsx" />

        }

        <main className={`flex-1 overflow-auto transition-all duration-300 ${
        isMobile ?
        'pb-20' // Add bottom padding on mobile for bottom nav
        : isSidebarCollapsed ?
        'ml-0' :
        'ml-0'}`
        } data-id="znxbdo1po" data-path="src/pages/TeacherApp.tsx">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`${isMobile ? 'p-3' : 'p-6'} max-w-7xl mx-auto`} data-id="1kj4j2c8e" data-path="src/pages/TeacherApp.tsx">

            {renderContent()}
          </motion.div>
        </main>

        {/* Mobile: Show bottom navigation */}
        {isMobile &&
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed} data-id="9dnzim3g6" data-path="src/pages/TeacherApp.tsx" />

        }
      </div>
      
      {/* Floating Chatbot - Available on all pages, adjusted for mobile */}
      <div className={isMobile ? 'mb-20' : ''} data-id="4ybabupfk" data-path="src/pages/TeacherApp.tsx">
        <FloatingChatbot ref={floatingChatbotRef} data-id="bxzqkoa39" data-path="src/pages/TeacherApp.tsx" />
      </div>
    </div>);

};

export default TeacherApp;